'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable, TextError } from '@/components/common/Error';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { getParsedValue } from '@/helpers/formatters';
import { isVaultV2ExitGated } from '@/helpers/vault';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import type { VaultV2ActionPanelData } from '../vaultV2ActionPanel.mapper';
import { useVaultV2WithdrawLogic } from '../../hooks/useVaultV2WithdrawLogic';
import ForceDeallocationOrderCallout from './ForceDeallocationOrderCallout';
import { useInKindRedemptionEntry } from './VaultV2InKindRedemptionDialog';
import { VaultV2WithdrawReviewDialog } from './VaultV2ReviewDialog';
import { VaultV2Summary } from './VaultV2Summary';
interface VaultV2OperationWithdrawProps {
  vaultData: VaultV2ActionPanelData;
  vaultAddress: Address;
}
const VaultV2OperationWithdraw = t0 => {
  const $ = _c(66);
  const {
    vaultData,
    vaultAddress
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const t1 = vaultData?.asset;
  let t2;
  if ($[0] !== t1) {
    t2 = getParsedValue(t1, parseApiAsset);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const vaultAsset = t2;
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.vaultBundlesV1", vaultAsset?.address, vaultData?.asset?.chain?.id);
  let t3;
  if ($[2] !== vaultAddress || $[3] !== vaultData) {
    t3 = {
      vaultData,
      vaultAddress
    };
    $[2] = vaultAddress;
    $[3] = vaultData;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const logic = useVaultV2WithdrawLogic(t3);
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    inputMax,
    finalPosition,
    isLoading,
    userVaultAssets,
    summaryFeeAssets,
    summaryForceDeallocation,
    summaryInKindRedemption,
    showForceDeallocationOrderCallout,
    showInKindRedemptionEntry,
    error,
    resetInput,
    refetchPosition,
    refetchVaultData
  } = logic;
  let t4;
  if ($[5] !== refetchPosition || $[6] !== refetchVaultData || $[7] !== resetInput) {
    t4 = () => {
      resetInput();
      refetchPosition();
      refetchVaultData();
    };
    $[5] = refetchPosition;
    $[6] = refetchVaultData;
    $[7] = resetInput;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onTxSuccess = t4;
  let t5;
  if ($[9] !== inputAmount || $[10] !== isMax || $[11] !== onTxSuccess || $[12] !== toggleDialog || $[13] !== userVaultAssets || $[14] !== vaultAddress || $[15] !== vaultData) {
    t5 = () => {
      if (!isDefined(userVaultAssets)) {
        return;
      }
      toggleDialog(<SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="494px" />}><VaultV2WithdrawReviewDialog vaultData={vaultData} vaultAddress={vaultAddress} inputAmount={inputAmount} isMax={isMax} onTxSuccess={onTxSuccess} entryPoint="vault_page" /></SectionErrorBoundary>);
    };
    $[9] = inputAmount;
    $[10] = isMax;
    $[11] = onTxSuccess;
    $[12] = toggleDialog;
    $[13] = userVaultAssets;
    $[14] = vaultAddress;
    $[15] = vaultData;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const openReviewDialog = t5;
  const t6 = vaultData?.asset.chain.id;
  let t7;
  if ($[17] !== logic || $[18] !== onTxSuccess || $[19] !== openReviewDialog || $[20] !== t6 || $[21] !== vaultAddress || $[22] !== vaultAsset) {
    t7 = {
      source: "vault_page",
      chainId: t6,
      vaultAddress,
      vaultAsset,
      logic,
      onReview: openReviewDialog,
      onTxSuccess
    };
    $[17] = logic;
    $[18] = onTxSuccess;
    $[19] = openReviewDialog;
    $[20] = t6;
    $[21] = vaultAddress;
    $[22] = vaultAsset;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const {
    liquidityCallout,
    actionButtonProps
  } = useInKindRedemptionEntry(t7);
  const t8 = `Withdraw ${vaultAsset?.symbol}`;
  const t9 = inputMax?.value;
  const t10 = vaultData?.gates;
  let t11;
  if ($[24] !== t10) {
    t11 = isVaultV2ExitGated(t10);
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== inputAmount || $[27] !== isLoading || $[28] !== isMax || $[29] !== onMaxClick || $[30] !== setInputAmount || $[31] !== t11 || $[32] !== t8 || $[33] !== t9 || $[34] !== vaultAsset) {
    t12 = <LargeAssetInput header={t8} token={vaultAsset} amount={inputAmount} onChange={setInputAmount} maxValue={t9} isMaxLoading={isLoading} isMax={isMax} onMaxClick={onMaxClick} disabled={t11} autoFocus={true} />;
    $[26] = inputAmount;
    $[27] = isLoading;
    $[28] = isMax;
    $[29] = onMaxClick;
    $[30] = setInputAmount;
    $[31] = t11;
    $[32] = t8;
    $[33] = t9;
    $[34] = vaultAsset;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== error.userVaultPositionError) {
    t13 = error.userVaultPositionError && <div className="flex justify-end"><TextError message="Error fetching vault position." messageTooltip={error.userVaultPositionError.message} /></div>;
    $[36] = error.userVaultPositionError;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t12 || $[39] !== t13) {
    t14 = <div className="gap-2xs flex flex-col">{t12}{t13}</div>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== showForceDeallocationOrderCallout) {
    t15 = showForceDeallocationOrderCallout && <ForceDeallocationOrderCallout />;
    $[41] = showForceDeallocationOrderCallout;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== finalPosition || $[44] !== isLoading || $[45] !== summaryFeeAssets || $[46] !== summaryForceDeallocation || $[47] !== summaryInKindRedemption || $[48] !== userVaultAssets || $[49] !== vaultAddress || $[50] !== vaultAsset || $[51] !== vaultData) {
    t16 = <VaultV2Summary vaultAsset={vaultAsset} vaultAddress={vaultAddress} vaultData={vaultData} initialPosition={userVaultAssets} finalPosition={finalPosition} isLoading={isLoading} feeAssets={summaryFeeAssets} forceDeallocation={summaryForceDeallocation} inKindRedemption={summaryInKindRedemption} />;
    $[43] = finalPosition;
    $[44] = isLoading;
    $[45] = summaryFeeAssets;
    $[46] = summaryForceDeallocation;
    $[47] = summaryInKindRedemption;
    $[48] = userVaultAssets;
    $[49] = vaultAddress;
    $[50] = vaultAsset;
    $[51] = vaultData;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== bundleCanTransfer || $[54] !== showInKindRedemptionEntry) {
    t17 = !showInKindRedemptionEntry && !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[53] = bundleCanTransfer;
    $[54] = showInKindRedemptionEntry;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  const t18 = actionButtonProps.isFinalizeDisabled || !showInKindRedemptionEntry && !bundleCanTransfer;
  let t19;
  if ($[56] !== actionButtonProps || $[57] !== t18) {
    t19 = <ActionButtons {...actionButtonProps} isFinalizeDisabled={t18} />;
    $[56] = actionButtonProps;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  let t20;
  if ($[59] !== liquidityCallout || $[60] !== t14 || $[61] !== t15 || $[62] !== t16 || $[63] !== t17 || $[64] !== t19) {
    t20 = <>{t14}{liquidityCallout}{t15}{t16}{t17}{t19}</>;
    $[59] = liquidityCallout;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = t19;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  return t20;
};
export default VaultV2OperationWithdraw;